// components/create/LocationSearchModal.js
//
// Finding the venue, through Google Places (New).
//
// ── History, briefly ───────────────────────────────────────────────────────
//
// This was a stub — `// TODO: Implement actual search` returning an empty array
// after half a second, which is why every query said "nothing found". It then
// briefly used Nominatim, to match the app. It now uses Google Places (New)
// through the `places-search` Edge Function, which is what the app is moving
// to as well.
//
// ── Type, then Search ──────────────────────────────────────────────────────
//
// Not autocomplete-as-you-type. Google bills autocomplete per session, and a
// session opens on the first keystroke whether or not anyone ever picks a
// place — so search-as-you-type charges for every abandoned search. A button
// means a session exists only once someone wants results. One session covers
// however many times they refine the query, and closes when they pick.
//
// ── Signed in only ─────────────────────────────────────────────────────────
//
// The Edge Function verifies the caller's JWT, so an anonymous visitor cannot
// spend the quota even by calling it directly. Signed out, this offers a plain
// text field instead — which also covers the venues that are on no map at all:
// someone's flat, a farmhouse, a hall with no listing.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FiX, FiSearch, FiMapPin } from 'react-icons/fi';
import { useAuth } from '../../services/auth';
import { createPlacesSession, embedMapUrl } from '../../services/placesService';
import '../../styles/LocationSearchModal.scss';
import Icon from '../Icon';

export default function LocationSearchModal({ onClose, onSelectLocation }) {
  const { user } = useAuth();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState(null);
  const [manual, setManual] = useState('');

  // Chosen but not yet confirmed, so the host can see the map before keeping it.
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // One session for the life of this sheet. See placesService.
  const sessionRef = useRef(null);
  if (!sessionRef.current) sessionRef.current = createPlacesSession();

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  const runSearch = useCallback(async () => {
    const q = query.trim();
    if (q.length < 2) return;

    setSearching(true);
    setError(null);
    setPreview(null);

    try {
      setResults(await sessionRef.current.search(q));
      setSearched(true);
    } catch (err) {
      setError(err.message);
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, [query]);

  const openPreview = async (suggestion) => {
    setLoadingPreview(true);
    setError(null);
    try {
      // A suggestion from the cache carries its own details, so this costs
      // nothing and opens no session.
      const place = await sessionRef.current.details(suggestion.placeId, suggestion.cached);
      setPreview(place);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingPreview(false);
    }
  };

  /** The shape CreateEvent saves from. */
  const keepPreview = () => {
    onSelectLocation({
      display_name: preview.name,
      formatted_address: preview.address,
      lat: preview.lat,
      lng: preview.lng,
      place_id: preview.placeId,
      source: 'google',
    });
    onClose();
  };

  const applyTyped = () => {
    const name = manual.trim();
    if (!name) return;
    onSelectLocation({
      display_name: name,
      formatted_address: name,
      lat: null,
      lng: null,
      place_id: null,
      source: 'manual',
    });
    onClose();
  };

  const manualBlock = (id, label, placeholder, hint) => (
    <div className="location-manual">
      <label htmlFor={id}>{label}</label>
      <div className="location-manual-row">
        <input
          id={id}
          type="text"
          value={manual}
          maxLength={120}
          placeholder={placeholder}
          onChange={(e) => setManual(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') applyTyped(); }}
        />
        <button type="button" onClick={applyTyped} disabled={!manual.trim()}>
          Use
        </button>
      </div>
      {hint && <small>{hint}</small>}
    </div>
  );

  const mapUrl = preview ? embedMapUrl(preview) : null;

  return (
    <div className="location-modal-overlay" onClick={onClose}>
      <div
        className="location-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Venue"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>{preview ? preview.name : 'Where is it?'}</h2>
          <button className="close-button" onClick={onClose} aria-label="Close">
            <Icon name="close" />
          </button>
        </div>

        <div className="location-modal-body">
          {error && <p className="location-error">{error}</p>}

          {preview ? (
            <>
              {/* The Maps Embed API: free, unmetered, and it shows Google's own
                  pin and label for the place rather than a marker dropped at a
                  coordinate. */}
              {mapUrl ? (
                <div className="location-map">
                  <iframe
                    title={`Map of ${preview.name}`}
                    src={mapUrl}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  />
                </div>
              ) : (
                <p className="location-note">
                  Map preview is not configured on this site, but the venue is saved
                  either way.
                </p>
              )}

              <p className="location-address">{preview.address}</p>

              <div className="location-preview-actions">
                <button type="button" className="location-secondary" onClick={() => setPreview(null)}>
                  Back
                </button>
                <button type="button" className="location-primary" onClick={keepPreview}>
                  Use this venue
                </button>
              </div>
            </>
          ) : user ? (
            <>
              <div className="search-box">
                <FiSearch />
                <input
                  type="search"
                  className="search-input"
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setSearched(false); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
                  placeholder="Name of the place, or an address"
                  aria-label="Search for a venue"
                  autoFocus
                />
                {query && (
                  <button type="button" onClick={() => { setQuery(''); setResults([]); setSearched(false); }} aria-label="Clear">
                    <FiX />
                  </button>
                )}
              </div>

              <button
                type="button"
                className="location-primary"
                onClick={runSearch}
                disabled={searching || query.trim().length < 2}
              >
                {searching ? 'Searching…' : 'Search'}
              </button>

              {loadingPreview ? (
                <p className="location-note">Loading that place…</p>
              ) : results.length > 0 ? (
                <ul className="location-results">
                  {results.map((item) => (
                    <li key={item.placeId}>
                      <button type="button" onClick={() => openPreview(item)}>
                        <FiMapPin />
                        <span className="location-result-text">
                          <strong>{item.mainText}</strong>
                          {item.secondaryText && <small>{item.secondaryText}</small>}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : searched && !searching ? (
                <p className="location-note">
                  Nothing found for “{query.trim()}”. Try the name on its own, or add
                  the city.
                </p>
              ) : (
                <p className="location-note">
                  Type the place and press Search. Results near you come first — add a
                  city to look further afield.
                </p>
              )}

              {manualBlock('venue-manual', 'Or type it yourself', "Ritu's place, 3rd floor")}
            </>
          ) : (
            <>
              <p className="location-note">
                Venue search needs an account. You can type the venue now and add the
                exact place and a map once you sign in — which happens when you
                publish anyway.
              </p>
              {manualBlock('venue-manual-out', 'Type the venue for now', "Ritu's place, Bandra")}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
